import React, { createContext, useContext, useReducer, useCallback } from 'react';

const AdminFormContext = createContext();

// Initial state
const initialState = {
  formData: {},
  validationErrors: {},
  isDirty: false
};

// Reducer for form state management
function formReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.field]: action.value
        },
        isDirty: true
      };
      
    case 'UPDATE_STEP_DATA':
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.data
        },
        isDirty: true
      };
      
    case 'SET_VALIDATION_ERROR':
      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          [action.field]: action.error
        }
      };
      
    case 'CLEAR_VALIDATION_ERROR':
      const newErrors = { ...state.validationErrors };
      delete newErrors[action.field];
      return {
        ...state,
        validationErrors: newErrors
      };
      
    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        validationErrors: {}
      };
      
    case 'RESET_FORM':
      return initialState;
      
    default:
      return state;
  }
}

export function AdminFormProvider({ children, initialFormData = {} }) {
  const [state, dispatch] = useReducer(formReducer, {
    ...initialState,
    formData: initialFormData
  });

  // Update a single field
  const updateField = useCallback((field, value) => {
    dispatch({ type: 'UPDATE_FIELD', field, value });
  }, []);

  // Update multiple fields at once
  const updateStepData = useCallback((data) => {
    dispatch({ type: 'UPDATE_STEP_DATA', data });
  }, []);

  // Set validation error for a field
  const setValidationError = useCallback((field, error) => {
    dispatch({ type: 'SET_VALIDATION_ERROR', field, error });
  }, []);

  // Clear validation error for a field
  const clearValidationError = useCallback((field) => {
    dispatch({ type: 'CLEAR_VALIDATION_ERROR', field });
  }, []);

  // Clear all validation errors
  const clearAllErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' });
  }, []);

  // Get field error
  const getFieldError = useCallback((field) => {
    return state.validationErrors[field];
  }, [state.validationErrors]);

  // Reset form
  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' });
  }, []);

  const contextValue = {
    formData: state.formData,
    validationErrors: state.validationErrors,
    isDirty: state.isDirty,
    updateField,
    updateStepData,
    setValidationError,
    clearValidationError,
    clearAllErrors,
    getFieldError,
    resetForm
  };

  return (
    <AdminFormContext.Provider value={contextValue}>
      {children}
    </AdminFormContext.Provider>
  );
}

export function useAdminFormContext() {
  const context = useContext(AdminFormContext);
  if (!context) {
    throw new Error('useAdminFormContext must be used within AdminFormProvider');
  }
  return context;
}